/**
 * The moment scheduler, held for one talk on one page.
 *
 * Everything the scheduler decides is pure (./arrival). This holds its state,
 * resolves the moment it names, remembers which ones this browser has already
 * shown, and owns the one thing the pure part cannot know: time passing, which
 * comes back as an `expire`. The room's theatre and the Watch page both hang
 * their player on it, so a poll the host placed at 2:54 lands at 2:54 wherever
 * the talk is played.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { WatchInteraction, WatchSession } from "../../shared/types";
import { api } from "../lib/api";
import {
  ANSWERED_STAY_SEC,
  type ArrivalEvent,
  type ArrivalMoment,
  type ArrivalState,
  arrivalReducer,
  emptyArrival,
  settleAnswer,
  shouldPause,
  staySeconds,
  upMoment,
  waitingCount,
} from "./arrival";
import type { AnswerFn, Arrivals } from "./TalkPlayer";

/**
 * Once per viewer, kept in this browser: a moment that has been up never comes
 * back, however many times they replay. Every call is wrapped, because a private
 * window or blocked site data throws on the accessor itself, and a viewer whose
 * browser will not remember still gets a working page.
 */
function momentKey(token: string, interactionId: string): string {
  return `fw.moment.${token}.${interactionId}`;
}
function momentAlreadyShown(token: string, interactionId: string): boolean {
  try {
    return window.localStorage.getItem(momentKey(token, interactionId)) !== null;
  } catch {
    return false;
  }
}
function rememberMomentShown(token: string, interactionId: string): void {
  try {
    window.localStorage.setItem(momentKey(token, interactionId), "1");
  } catch {
    /* nothing to remember with; the moment simply may arrive again next visit */
  }
}

export function useArrivals({
  token,
  session,
  active,
  setMine,
}: {
  token: string;
  session: WatchSession;
  /** Is the talk on screen? The seconds a card holds the screen only run while it is. */
  active: boolean;
  /** Write this viewer's answer onto the moment, wherever the page keeps the session. */
  setMine: (interactionId: string, value: WatchInteraction["my_response"]) => void;
}): {
  arrivals: Arrivals;
  answer: AnswerFn;
  again: (id: string) => void;
  /** A live's moment is up now, or the host closed or skipped it. */
  due: (id: string) => void;
  close: (id: string) => void;
} {
  const moments = useMemo<ArrivalMoment[]>(
    () => session.interactions.map((i) => ({ id: i.id, appears_at: i.appears_at, show: i.show })),
    [session.interactions],
  );
  const momentsRef = useRef(moments);
  momentsRef.current = moments;
  const [arrival, setArrival] = useState<ArrivalState>(() =>
    emptyArrival(
      session.interactions
        .filter((i) => i.answered || momentAlreadyShown(token, i.id))
        .map((i) => i.id),
    ),
  );
  const fire = useCallback((event: ArrivalEvent) => {
    setArrival((state) => arrivalReducer(state, momentsRef.current, event));
  }, []);

  const upId = arrival.up;
  const upAnswered = arrival.answered;

  /**
   * A moment is used up only once it is really over: dismissed, run out, or
   * answered with the answer safely on the server.
   */
  const finishMoment = useCallback(
    (event: Extract<ArrivalEvent, { id: string }>) => {
      rememberMomentShown(token, event.id);
      fire(event);
    },
    [token, fire],
  );

  // The stay: the seconds a card holds the screen only run while the talk is up,
  // so closing the theatre holds them rather than spending them.
  const stayLeft = useRef<{ key: string; ms: number } | null>(null);
  useEffect(() => {
    if (!upId) {
      stayLeft.current = null;
      return;
    }
    const show = momentsRef.current.find((m) => m.id === upId)?.show;
    const seconds = upAnswered ? ANSWERED_STAY_SEC : show ? staySeconds(show) : null;
    if (seconds === null) {
      stayLeft.current = null;
      return;
    }
    const key = `${upId}:${upAnswered}`;
    if (stayLeft.current?.key !== key) stayLeft.current = { key, ms: seconds * 1000 };
    if (!active) return;
    const held = stayLeft.current;
    const startedAt = Date.now();
    const timer = window.setTimeout(() => finishMoment({ type: "expire", id: upId }), held.ms);
    return () => {
      window.clearTimeout(timer);
      if (stayLeft.current === held) held.ms = Math.max(0, held.ms - (Date.now() - startedAt));
    };
  }, [upId, upAnswered, active, finishMoment]);

  // Persist a poll pick / question answer / reaction / handout download.
  const answer = useCallback<AnswerFn>(
    (interactionId, value) => {
      if (!value) return;
      const prev = session.interactions.find((i) => i.id === interactionId)?.my_response ?? null;
      void settleAnswer({
        optimistic: () => setMine(interactionId, value),
        post: () => api.watch.respond(token, { interaction_id: interactionId, value }),
        settled: () => finishMoment({ type: "answer", id: interactionId }),
        failed: () => setMine(interactionId, prev),
      });
    },
    [token, session.interactions, setMine, finishMoment],
  );

  const arrivals = useMemo<Arrivals>(() => {
    const up = upMoment(arrival, moments);
    return {
      up: up ? (session.interactions.find((i) => i.id === up.id) ?? null) : null,
      waiting: waitingCount(arrival),
      paused: shouldPause(arrival, moments),
      onTime: (tick) => fire({ type: "time", ...tick }),
      dismiss: (id) => finishMoment({ type: "dismiss", id }),
    };
  }, [arrival, moments, session.interactions, fire, finishMoment]);

  // Asked for again from its dot: only one they have not answered comes back.
  const again = useCallback(
    (id: string) => {
      const moment = session.interactions.find((i) => i.id === id);
      if (moment && moment.my_response === null) fire({ type: "again", id });
    },
    [session.interactions, fire],
  );

  const due = useCallback((id: string) => fire({ type: "due", id }), [fire]);
  const close = useCallback((id: string) => finishMoment({ type: "close", id }), [finishMoment]);

  return { arrivals, answer, again, due, close };
}
